import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Briefcase, 
  Users, 
  UserCheck, 
  ChevronRight, 
  Plus, 
  Zap,
  Trash2,
  Edit,
  ChevronUp,
  ChevronDown,
  Check,
  ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import type { StageData, ApproverUser, Rule, RuleExecutionTiming, StageOption } from './types';
import { cn } from '@/lib/utils';
import { RuleDrawer } from './RuleDrawer';
import { getRuleConditionSummary, getRuleActionSummary } from './utils';

interface StageDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingStage?: StageData | null;
  onSave: (stageData: StageData) => void;
  onSaveAndAddAnother?: () => void;
  initialStageName?: string;
  approverUsers?: ApproverUser[];
  workflowId?: string;
  workflowName?: string;
  availableStages?: StageOption[];
}

interface FormErrors {
  stageName?: string;
  approverType?: string;
  specificPerson?: string;
}

// Force HMR update - checkbox is correct
export function StageDrawer({
  open,
  onOpenChange,
  existingStage,
  onSave,
  onSaveAndAddAnother,
  initialStageName,
  approverUsers = [],
  workflowId,
  workflowName,
  availableStages = [],
}: StageDrawerProps) {
  // Form state
  const [stageName, setStageName] = useState('');
  const [description, setDescription] = useState('');
  const [approverType, setApproverType] = useState<'DirectManager' | 'ProjectManager' | 'DepartmentHead' | 'SpecificPerson'>('DirectManager');
  const [specificPersonId, setSpecificPersonId] = useState('');
  const [specificPersonPickerOpen, setSpecificPersonPickerOpen] = useState(false);
  const [specificPersonSearch, setSpecificPersonSearch] = useState('');
  const [cannotApproveOwn, setCannotApproveOwn] = useState(true); // Default to true for safety
  const [allowApproverEdit, setAllowApproverEdit] = useState(false); // Default to false
  const [smartRulesExpanded, setSmartRulesExpanded] = useState(false);
  const [rules, setRules] = useState<Rule[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const selectedSpecificPerson = approverUsers.find((user) => user.id === specificPersonId);

  const filteredApproverUsers = useMemo(() => {
    const query = specificPersonSearch.trim().toLowerCase();
    if (!query) return approverUsers;
    return approverUsers.filter((user) => user.name.toLowerCase().includes(query));
  }, [approverUsers, specificPersonSearch]);
  
  // Rule builder dialog state
  const [ruleBuilderOpen, setRuleBuilderOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | undefined>(undefined);
  const [ruleExecutionTiming, setRuleExecutionTiming] = useState<RuleExecutionTiming>('pre-approval');
  const [ruleContext, setRuleContext] = useState<'approved' | 'rejected' | undefined>(undefined);

  // Initialize form with existing stage data or defaults
  useEffect(() => {
    if (open) {
      if (existingStage) {
        setStageName(existingStage.basicDetails.stageName);
        setDescription(existingStage.basicDetails.description || '');
        
        // Map approver type from the separate approver field
        if (existingStage.approver.approverType === 'role') {
          setApproverType(existingStage.approver.roleType || 'DirectManager');
        } else if (existingStage.approver.approverType === 'user') {
          setApproverType('SpecificPerson');
          setSpecificPersonId(existingStage.approver.specificUserId || '');
        }
        
        setCannotApproveOwn(existingStage.approver.cannotApproveOwn);
        setAllowApproverEdit(existingStage.allowApproverEdit || false);
        setRules(existingStage.smartRules?.rules || []);
      } else {
        // Reset form for new stage
        setStageName(initialStageName || '');
        setDescription('');
        setApproverType('DirectManager');
        setSpecificPersonId('');
        setCannotApproveOwn(true); // Default to true for safety
        setAllowApproverEdit(false); // Default to false
        setRules([]);
      }
      setErrors({});
      setSmartRulesExpanded(false);
    }
  }, [open, existingStage, initialStageName]);

  // Validation
  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!stageName.trim()) {
      newErrors.stageName = 'Stage name is required';
    }

    if (!approverType) {
      newErrors.approverType = 'Please select an approver type';
    }

    if (approverType === 'SpecificPerson' && !specificPersonId) {
      newErrors.specificPerson = 'Please select a specific person';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);

    try {
      const stageData: StageData = {
        basicDetails: {
          stageName: stageName.trim(),
          description: description.trim(),
        },
        approver: {
          approverType: approverType === 'SpecificPerson' ? 'user' : 'role',
          roleType: approverType !== 'SpecificPerson' ? approverType : undefined,
          specificUserId: approverType === 'SpecificPerson' ? specificPersonId : undefined,
          cannotApproveOwn: cannotApproveOwn,
        },
        allowApproverEdit: allowApproverEdit,
        smartRules: {
          rules,
          onApproved: { type: 'continue' },
          onRejected: { type: 'reject' },
        },
        notifications: {
          onApproved: [],
          onRejected: [],
        },
      };

      onSave(stageData);
      toast.success(existingStage ? 'Stage updated successfully' : 'Stage created successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving stage:', error);
      toast.error('Failed to save stage');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save and add another
  const handleSaveAndAddAnother = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);

    try {
      const stageData: StageData = {
        basicDetails: {
          stageName: stageName.trim(),
          description: description.trim(),
        },
        approver: {
          approverType: approverType === 'SpecificPerson' ? 'user' : 'role',
          roleType: approverType !== 'SpecificPerson' ? approverType : undefined,
          specificUserId: approverType === 'SpecificPerson' ? specificPersonId : undefined,
          cannotApproveOwn: cannotApproveOwn,
        },
        allowApproverEdit: allowApproverEdit,
        smartRules: {
          rules,
          onApproved: { type: 'continue' },
          onRejected: { type: 'reject' },
        },
        notifications: {
          onApproved: [],
          onRejected: [],
        },
      };

      onSave(stageData);
      
      if (onSaveAndAddAnother) {
        onSaveAndAddAnother();
      }
      
      toast.success('Stage created successfully');
      
      // Reset form for next stage
      setStageName('');
      setDescription('');
      setApproverType('DirectManager');
      setSpecificPersonId('');
      setCannotApproveOwn(true); // Default to true for safety
      setAllowApproverEdit(false); // Default to false
      setRules([]);
      setErrors({});
    } catch (error) {
      console.error('Error saving stage:', error);
      toast.error('Failed to save stage');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false);
  };

  // Rule handlers
  const handleAddRule = (executionTiming: RuleExecutionTiming, context?: 'approved' | 'rejected') => {
    setEditingRule(undefined);
    setRuleExecutionTiming(executionTiming);
    setRuleContext(context);
    setRuleBuilderOpen(true);
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setRuleExecutionTiming(rule.executionTiming);
    setRuleContext(rule.context);
    setRuleBuilderOpen(true);
  };

  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(r => r.id !== ruleId));
    toast.success('Rule deleted');
  };

  const handleReorderRule = (ruleId: string, direction: 'up' | 'down') => {
    const index = rules.findIndex(r => r.id === ruleId);
    if (index === -1) return;

    const newRules = [...rules];
    if (direction === 'up' && index > 0) {
      [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
      toast.success('Rule moved up');
    } else if (direction === 'down' && index < newRules.length - 1) {
      [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
      toast.success('Rule moved down');
    }
    setRules(newRules);
  };

  const handleSaveRule = (rule: Rule) => {
    if (editingRule) {
      // Update existing rule
      setRules(rules.map(r => r.id === rule.id ? rule : r));
      toast.success('Rule updated');
    } else {
      // Add new rule
      setRules([...rules, rule]);
      toast.success('Rule added');
    }
    setRuleBuilderOpen(false);
    setEditingRule(undefined);
  };

  // Approver type options
  const approverOptions = [
    {
      value: 'DirectManager' as const,
      label: 'Direct Manager',
      description: 'The employee\'s direct line manager',
      icon: User,
    },
    {
      value: 'ProjectManager' as const,
      label: 'Project Manager',
      description: 'The manager of the project being worked on',
      icon: Briefcase,
    },
    {
      value: 'DepartmentHead' as const,
      label: 'Department Head',
      description: 'The head of the employee\'s department',
      icon: Users,
    },
    {
      value: 'SpecificPerson' as const,
      label: 'Specific Person',
      description: 'A designated individual',
      icon: UserCheck,
    },
  ];

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-[600px] overflow-y-auto"
      >
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle>
            {existingStage ? 'Edit Approval Stage' : 'Add Approval Stage'}
          </SheetTitle>
          <SheetDescription>
            {existingStage 
              ? 'Update the stage configuration below' 
              : 'Configure the details for this approval stage'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 pb-6">
          {/* Stage Details Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-3">Stage Details</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="stageName">
                    Stage Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="stageName"
                    placeholder="e.g., Manager Review"
                    value={stageName}
                    onChange={(e) => setStageName(e.target.value)}
                    className={cn(errors.stageName && 'border-destructive')}
                  />
                  {errors.stageName && (
                    <p className="text-xs text-destructive">{errors.stageName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of this approval stage"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Approver Section */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">
                  Who Should Approve? <span className="text-destructive">*</span>
                </Label>
                {errors.approverType && (
                  <p className="text-xs text-destructive mt-1">{errors.approverType}</p>
                )}
              </div>

              <RadioGroup
                value={approverType}
                onValueChange={(value) => setApproverType(value as typeof approverType)}
                className="space-y-3"
              >
                {approverOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        'flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors',
                        approverType === option.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      )}
                      onClick={() => setApproverType(option.value)}
                    >
                      <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <Label
                            htmlFor={option.value}
                            className="font-medium cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </RadioGroup>

              {/* Specific Person Selector */}
              {approverType === 'SpecificPerson' && (
                <div className="space-y-2 pl-7">
                  <Label htmlFor="specificPerson">
                    Select Person <span className="text-destructive">*</span>
                  </Label>
                  <Popover
                    open={specificPersonPickerOpen}
                    onOpenChange={(isOpen) => {
                      setSpecificPersonPickerOpen(isOpen);
                      if (!isOpen) setSpecificPersonSearch('');
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        id="specificPerson"
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={specificPersonPickerOpen}
                        className={cn(
                          'w-full justify-between font-normal',
                          !selectedSpecificPerson && 'text-muted-foreground',
                          errors.specificPerson && 'border-destructive'
                        )}
                      >
                        <span className="truncate">
                          {selectedSpecificPerson?.name ?? 'Choose a person...'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0"
                      style={{ width: 'var(--radix-popover-trigger-width)' }}
                      align="start"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search people..."
                          value={specificPersonSearch}
                          onValueChange={setSpecificPersonSearch}
                        />
                        <CommandList className="max-h-[300px]">
                          <CommandEmpty>
                            {approverUsers.length === 0 ? 'No users available' : 'No people found.'}
                          </CommandEmpty>
                          {filteredApproverUsers.length > 0 && (
                            <CommandGroup>
                              {filteredApproverUsers.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  value={user.id}
                                  onSelect={() => {
                                    setSpecificPersonId(user.id);
                                    setSpecificPersonPickerOpen(false);
                                    setSpecificPersonSearch('');
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      specificPersonId === user.id ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                  <span className="truncate">{user.name}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {errors.specificPerson && (
                    <p className="text-xs text-destructive">{errors.specificPerson}</p>
                  )}
                </div>
              )}

              {/* Cannot Approve Own */}
              <div className="flex items-start space-x-2 pl-7">
                <Checkbox
                  id="cannotApproveOwn"
                  checked={cannotApproveOwn}
                  onCheckedChange={(checked) => setCannotApproveOwn(checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="cannotApproveOwn"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Prevent approver from approving their own timesheet
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, approvers cannot approve timesheets they submitted
                  </p>
                </div>
              </div>

              {/* Allow Approver Edit */}
              <div className="flex items-start space-x-2 pl-7">
                <Checkbox
                  id="allowApproverEdit"
                  checked={allowApproverEdit}
                  onCheckedChange={(checked) => setAllowApproverEdit(checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="allowApproverEdit"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Allow approver to edit timesheets
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, approvers can make changes to timesheets during approval
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Advanced Options - Collapsible */}
          <Collapsible open={smartRulesExpanded} onOpenChange={setSmartRulesExpanded}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent/50 rounded-md px-2 transition-colors">
              <div className="flex items-center gap-2">
                <ChevronRight 
                  className={cn(
                    "h-4 w-4 transition-transform",
                    smartRulesExpanded && "rotate-90"
                  )} 
                />
                <span className="text-sm font-medium">Advanced Options</span>
                {rules.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({rules.length} rule{rules.length !== 1 ? 's' : ''} configured)
                  </span>
                )}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4 space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium mb-1">Smart Rules</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add conditional routing and automated actions
                  </p>
                  
                  {/* Add Rule Buttons */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {/* Before Stage Button */}
                    <button
                      onClick={() => handleAddRule('pre-approval')}
                      className="flex flex-col items-start p-3 bg-card rounded-lg border-2 border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-left"
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Plus className="h-3 w-3 text-blue-400" />
                        <span className="text-base">🔵</span>
                        <span className="font-semibold text-xs">Before Stage</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Validates before stage begins
                      </p>
                    </button>

                    {/* After Decision Button */}
                    <button
                      onClick={() => handleAddRule('post-approval', 'approved')}
                      className="flex flex-col items-start p-3 bg-card rounded-lg border-2 border-green-500/30 hover:border-green-500/50 hover:bg-green-500/5 transition-all text-left"
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Plus className="h-3 w-3 text-green-400" />
                        <span className="text-base">🟢</span>
                        <span className="font-semibold text-xs">After Decision</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Routes after approve/reject
                      </p>
                    </button>
                  </div>
                </div>

                {rules.length === 0 ? (
                  <Alert>
                    <Zap className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      No rules configured. Smart rules allow you to automate routing based on conditions like project type, amount, or employee role.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-2">
                    {rules.map((rule, index) => {
                      const conditionSummary = getRuleConditionSummary(rule.condition);
                      const actionSummary = getRuleActionSummary(rule.ifTrue);
                      
                      return (
                        <div
                          key={rule.id}
                          className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium truncate">{rule.name}</p>
                              <Badge variant={rule.executionTiming === 'pre-approval' ? 'default' : 'secondary'} className="text-xs">
                                {rule.executionTiming === 'pre-approval' ? 'Pre-Approval' : 'Post-Approval'}
                              </Badge>
                              {rule.context && (
                                <Badge variant="outline" className="text-xs">
                                  {rule.context === 'approved' ? 'On Approved' : 'On Rejected'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              If {conditionSummary}, then {actionSummary}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReorderRule(rule.id, 'up')}
                              disabled={index === 0}
                              title="Move up"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReorderRule(rule.id, 'down')}
                              disabled={index === rules.length - 1}
                              title="Move down"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditRule(rule)}
                              title="Edit rule"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRule(rule.id)}
                              title="Delete rule"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <SheetFooter className="gap-2 px-6 pb-6 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : existingStage ? 'Update Stage' : 'Add Stage'}
          </Button>
          {!existingStage && onSaveAndAddAnother && (
            <Button 
              variant="secondary" 
              onClick={handleSaveAndAddAnother}
              disabled={isSaving}
            >
              Save & Add Another
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {/* Rule Drawer */}
    <RuleDrawer
      isOpen={ruleBuilderOpen}
      onClose={() => {
        setRuleBuilderOpen(false);
        setEditingRule(undefined);
      }}
      onSave={handleSaveRule}
      existingRule={editingRule}
      executionTiming={ruleExecutionTiming}
      context={ruleContext}
      availableStages={availableStages}
      workflowContext={{
        workflowId: workflowId || '',
        workflowName: workflowName || 'Workflow',
        currentStageNumber: 1,
        currentStageId: existingStage?.basicDetails?.stageName || '',
        currentStageName: existingStage?.basicDetails?.stageName || 'New Stage',
        totalStages: availableStages.length || 1,
        allStages: availableStages.map((s, i) => ({ id: s.id, name: s.name, order: i + 1 })),
      }}
    />
  </>
  );
}
